import type { MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { AvatarWithStatus } from '@/components/common/avatar-with-status';
import type { GroupMember } from '@/models/group/group-info';
import { useAuthStore } from '@/stores/auth';
import cn from '@/utils/merge-class-names';

interface Props {
  member: GroupMember;
  canManage?: boolean;
  showBadge?: boolean;
  onSummonContextMenu: (e: MouseEvent) => void;
}

export function GroupAdminListItem({ member, canManage, showBadge, onSummonContextMenu }: Props) {
  const userId = useAuthStore((s) => s.auth?.UserID);
  const { t } = useTranslation();

  return (
    <div
      className='flex min-w-0 flex-1 items-center gap-4 focus:bg-primary/20'
      onClick={canManage ? onSummonContextMenu : undefined}>
      <AvatarWithStatus
        imageUrl={member.UserHeadImg}
        className='size-12'
        size={[48, 48]}
        label={member.UserNickName}
        isSelf={userId === member.UserID}
        isOnline={member.OnLine === 1}
        defaultAvatar='group'
      />

      <div className='line-clamp-1 flex flex-1 flex-col items-stretch justify-center gap-0.5'>
        <h5 className='truncate break-all text-h2  desktop:text-body1'>{member.DisplayName}</h5>

        {member.RemarkName && (
          <span className='truncate break-all text-h2 desktop:text-sm text-neutral-400'>
            {member.GroupMemberName || member.UserNickName}
          </span>
        )}
      </div>

      <span
        className={cn('badge badge-outline badge-lg min-w-16 text-xs', {
          'text-secondary': member.Role === 10,
          'text-primary': member.Role === 9,
          hidden: !showBadge,
        })}>
        {member.Role === 9
          ? t('conversation.setting.group.administrator')
          : member.Role === 10
            ? t('conversation.setting.group.owner')
            : null}
      </span>
    </div>
  );
}
